import { usePermissions as usePermissionsContext } from '../contexts/PermissionContext';
import { SubscriptionStatus } from '../services/paymentService';

// Feature type is now dynamic - any feature string from the database is valid
export type Feature = string;

interface UsePermissionsReturn {
  hasFeature: (feature: Feature) => boolean;
  tier: string;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  subscriptionStatus: SubscriptionStatus | null;
}

/**
 * Hook for checking user permissions and features based on subscription tier.
 * Now uses PermissionContext for shared state management.
 * 
 * @example
 * const { hasFeature, tier, isLoading } = usePermissions();
 * 
 * if (hasFeature('upload_defaults')) {
 *   // Show upload defaults component
 * }
 * 
 * if (hasFeature('scheduled_uploads')) {
 *   // Enable scheduled upload toggle
 * }
 */
export function usePermissions(): UsePermissionsReturn {
  const context = usePermissionsContext();
  
  // Return context value with subscriptionStatus for backward compatibility
  // Note: subscriptionStatus is not directly available in PermissionContext,
  // but we can reconstruct it from tier and features
  const subscriptionStatus: SubscriptionStatus | null = context
    ? {
        subscription_tier: context.tier,
        subscription_status: 'active', // Default, actual status not in PermissionContext
        is_complimentary: false,
        features: context.features,
      }
    : null;

  return {
    hasFeature: context.hasFeature,
    tier: context.tier,
    isLoading: context.isLoading,
    error: context.error,
    refresh: context.refresh,
    subscriptionStatus,
  };
}
